import { useCallback, useEffect, useRef, useState } from 'react'
import {
  deleteScorecard,
  fetchScorecardUrl,
  listScorecards,
  prettySize,
  uploadScorecard,
  type ScorecardSummary,
} from '../lib/scorecardApi'
import { shopDate } from '../lib/grouping'
import PdfView from './PdfView'
import KpiScorecard from './KpiScorecard'

interface Props {
  token: string
  isAdmin: boolean
}

export default function Scorecard({ token, isAdmin }: Props) {
  const [items, setItems] = useState<ScorecardSummary[] | null>(null)
  const [viewing, setViewing] = useState<{
    id: string
    url: string
    bytes: Uint8Array
    fileName: string
  } | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  // Collapsed by default: the newest scorecard is already on screen above, so
  // this list only exists for reaching an older one. Left open it would push
  // the actual scorecard off the page once a few weeks have accumulated.
  const [listOpen, setListOpen] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  // A blob URL stays alive until it is revoked. Without this, opening five
  // scorecards in a meeting leaks all five for the life of the page.
  const showFile = useCallback(
    async (id: string) => {
      setProblem(null)
      try {
        const next = await fetchScorecardUrl(token, id)
        setViewing((prev) => {
          if (prev) URL.revokeObjectURL(prev.url)
          return { id, ...next }
        })
      } catch (err) {
        setProblem(err instanceof Error ? err.message : String(err))
      }
    },
    [token],
  )

  const load = useCallback(
    async (openNewest: boolean) => {
      try {
        const rows = await listScorecards(token)
        setItems(rows)
        const newest = rows[0]
        if (openNewest && newest) await showFile(newest.id)
      } catch (err) {
        setItems([])
        setProblem(err instanceof Error ? err.message : String(err))
      }
    },
    [token, showFile],
  )

  useEffect(() => {
    // Open the newest automatically: in a meeting everyone wants the current
    // one on screen without hunting for it.
    void load(true)
  }, [load])

  useEffect(() => () => {
    if (viewing) URL.revokeObjectURL(viewing.url)
  }, [viewing])

  const accept = useCallback(
    async (file: File | undefined | null) => {
      if (!file) return
      setBusy(true)
      setProblem(null)
      try {
        // No label — the upload date is what people actually identify these by,
        // and typing "Week of Aug 17" every week is a chore nobody wants.
        await uploadScorecard(token, file, null)
        await load(true)
      } catch (err) {
        setProblem(err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    },
    [token, load],
  )

  // Paste still works even though there is no visible drop box — it costs
  // nothing and is the fastest way in when the PDF is already on the clipboard.
  useEffect(() => {
    if (!isAdmin) return
    const onPaste = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? [])[0]
      if (file) {
        e.preventDefault()
        void accept(file)
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [isAdmin, accept])

  const remove = async (item: ScorecardSummary) => {
    setBusy(true)
    setProblem(null)
    try {
      await deleteScorecard(token, item.id)
      if (viewing?.id === item.id) {
        URL.revokeObjectURL(viewing.url)
        setViewing(null)
      }
      await load(true)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (items === null) return <p className="muted">Loading…</p>

  return (
    <>
      {/* The PDF leads: it is what the meeting opens on. The live KPI numbers
          each technician enters sit below it. */}
      <h2 className="group__label">Weekly PDF</h2>
      <section
        className={dragging ? 'sc sc--drag' : 'sc'}
        onDragOver={
          isAdmin
            ? (e) => {
                e.preventDefault()
                setDragging(true)
              }
            : undefined
        }
        onDragLeave={isAdmin ? () => setDragging(false) : undefined}
        onDrop={
          isAdmin
            ? (e) => {
                e.preventDefault()
                setDragging(false)
                void accept(e.dataTransfer.files[0])
              }
            : undefined
        }
      >
        {problem && <p className="banner">{problem}</p>}

        {isAdmin && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf"
              hidden
              onChange={(e) => {
                void accept(e.target.files?.[0])
                e.target.value = ''
              }}
            />
            <button
              type="button"
              className="btn btn--spaced"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {busy ? 'Uploading…' : 'Upload this week’s scorecard'}
            </button>
          </>
        )}

        {items.length === 0 && (
          <div className="panel panel--flat">
            <p className="muted">
              {isAdmin
                ? 'No scorecards yet. Upload this week’s PDF above.'
                : 'No scorecard has been posted yet.'}
            </p>
          </div>
        )}

        {viewing && (
          <div className="viewer">
            {/* Was an <iframe>. Mobile browsers — Chrome on Android and Safari on
                iOS alike — refuse to render a PDF in one and show a broken-document
                placeholder instead. PdfView draws the pages itself. */}
            <PdfView bytes={viewing.bytes} url={viewing.url} fileName={viewing.fileName} />
          </div>
        )}

        {items.length > 0 && (
          <div className="fold">
            <button
              type="button"
              className="fold__head"
              aria-expanded={listOpen}
              onClick={() => setListOpen((o) => !o)}
            >
              <span className="fold__caret" aria-hidden="true">
                {listOpen ? '▾' : '▸'}
              </span>
              Posted scorecards
              <span className="group__count">{items.length}</span>
            </button>

            {listOpen && (
              <div className="fold__body">
                {items.map((s) => (
                  <article
                    key={s.id}
                    className={'card' + (viewing?.id === s.id ? ' card--current' : '')}
                  >
                    <div className="card__head">
                      <h3 className="card__title">
                        {/* Titled by date. With no label the old fallback was the
                            filename, which phones generate as a bare UUID. */}
                        {s.label ?? `Uploaded ${shopDate(s.uploaded_at)}`}
                        {viewing?.id === s.id && (
                          <span className="chip chip--low">Showing</span>
                        )}
                      </h3>
                    </div>
                    <p className="card__due">
                      {prettySize(s.byte_size)}
                      {s.uploaded_by ? ` · ${s.uploaded_by}` : ''}
                    </p>
                    <div className="card__actions">
                      <button
                        type="button"
                        className="btn btn--quiet"
                        onClick={() => void showFile(s.id)}
                      >
                        {viewing?.id === s.id ? 'Reload' : 'View'}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn--quiet"
                          disabled={busy}
                          onClick={() => void remove(s)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* The live, editable half of the 90.io scorecard — a number each
          technician owns and enters themselves. */}
      <h2 className="group__label" style={{ marginTop: 28 }}>
        Team scorecard
      </h2>
      <KpiScorecard token={token} isAdmin={isAdmin} />
    </>
  )
}
